import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withDevtools } from '@pc/store';
import { IProduct } from '../../../shared/models/quote/basic-quote.interface';
import { computed, inject, Signal } from '@angular/core';
import { BenefitService } from '../../../shared/services/benefit.service';
import { IBenefitDetails } from '../../../shared/models/benefit.interface';
import { CoverType, ICover } from '../../../shared/models/cover.interface';
import { DestinationsStore } from '../../trip-details/where-are-you-travelling/store/destinations.store';

interface ProductsState {
  products: IProduct[];
  benefits: IBenefitDetails[];
  isProductProgrammaticallyAdded: boolean;
  selectedProductId: number | null;
  isLoading: boolean;
}

const initialState: ProductsState = {
  products: [],
  benefits: [],
  isProductProgrammaticallyAdded: false,
  selectedProductId: null,
  isLoading: false
};

export const ProductsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ProductsStore'),
  withState(initialState),
  withComputed((state) => ({
    selectedProduct: computed(() => {
      return state.products().find((product) => product.policyGroupID === state.selectedProductId());
    })
  })),
  withComputed((state) => {
    const covers = computed(() => state.selectedProduct()?.covers ?? []);

    // Helper to create computed signals for covers filtered by type
    const filterCovers = (type: CoverType): Signal<ICover[]> => {
      return computed(() => covers().filter((cover) => cover.coverType === type));
    };

    return {
      selectedProduct: state.selectedProduct,
      hiddenCovers: filterCovers(CoverType.hidden),
      coreCovers: filterCovers(CoverType.core),
      optionalCovers: filterCovers(CoverType.optional),
      activityCovers: filterCovers(CoverType.activity),
      allCovers: covers,
      excessLevels: computed(() => state.selectedProduct()?.excessLevels ?? []),
      insuredAmounts: computed(() => state.selectedProduct()?.insuredAmountData ?? []),
      selectedExcessId: computed(
        () => state.selectedProduct()?.excessLevels.find((excessLevel) => excessLevel.IsSelected)?.ID
      ),
      selectedInsuredAmountId: computed(
        () => state.selectedProduct()?.insuredAmountData.find((insuredAmount) => insuredAmount.IsSelected)?.ID
      )
    };
  }),

  withMethods((store, benefitService = inject(BenefitService), destinationsStore = inject(DestinationsStore)) => ({
    reset(): void {
      patchState(store, initialState);
    },

    setIsLoading(isLoading: boolean): void {
      patchState(store, { isLoading: isLoading });
    },

    setSelectedProductId(selectedProductId: number): void {
      if (store.selectedProductId() === selectedProductId) return;

      benefitService.getAllBenefitDetails(selectedProductId, destinationsStore.isDomestic()).subscribe((benefits) => {
        patchState(store, { benefits });
      });

      patchState(store, { selectedProductId });
    },
    setProducts(products: IProduct[]): void {
      patchState(store, { products });
    },
    setProductAddedProgrammatically(isProductProgrammaticallyAdded: boolean): void {
      patchState(store, { isProductProgrammaticallyAdded });
    },
    setInsuredAmount(insuredAmountId: number): void {
      const product = store.selectedProduct();
      if (!product) return;

      product.insuredAmountData.forEach((insuredAmount) => {
        insuredAmount.IsSelected = insuredAmount.ID === insuredAmountId;
      });

      patchState(store, { products: [...store.products()] });
    },
    setExcessLevel(excessLevelId: number): void {
      const product = store.selectedProduct();
      if (!product) return;

      product.excessLevels.forEach((excessLevel) => {
        excessLevel.IsSelected = excessLevel.ID === excessLevelId;
      });

      patchState(store, { products: [...store.products()] });
    },
    addCoversToProduct(productId: number, covers: ICover[]): void {
      const product = store.products().find((product) => product.policyGroupID === productId);
      if (!product) return;

      covers.forEach((cover) => {
        if (!product.covers.some((existingCover) => existingCover.id === cover.id)) {
          product.covers.push(cover);
        }
      });

      patchState(store, { products: [...store.products()] });
    }
  }))
);
